import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  ApprovePriceChangeFailure,
  ApprovePriceChangeInput,
  ApprovePriceChangeMutation,
} from "@/graphql/codegen/graphql"
import { ApprovePriceChangeDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import "@/screens/Pricing/graphql/mutations"

interface UseApprovePriceChangeSubmissionReturn {
  approvePriceChangeSubmission: (input: ApprovePriceChangeInput) => Promise<boolean>
  isApproving: boolean
}

export const useApprovePriceChangeSubmission = (): UseApprovePriceChangeSubmissionReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, approvePriceChange] = useMutation<
    ApprovePriceChangeMutation,
    { input: ApprovePriceChangeInput }
  >(ApprovePriceChangeDocument)

  const approvePriceChangeSubmission = useCallback(
    async (input: ApprovePriceChangeInput): Promise<boolean> => {
      const { data, error } = await approvePriceChange({ input })
      const output = data?.priceChange.approvePriceChange
      const hasError = error || output?.__typename === "ApprovePriceChangeFailure"

      if (hasError) {
        const message = error?.message ?? (output as ApprovePriceChangeFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }
      notify({ type: NotificationType.Success, message: "Price changes approved" })
      return true
    },
    [approvePriceChange, notify]
  )

  return { approvePriceChangeSubmission, isApproving: fetching }
}
